// Finance view of the payout ledger (paygate): every payout batch that left the
// company — payroll net pay, loan/advance disbursements — with per-employee status.
// Read-only; account numbers arrive already masked from paygate.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { X, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { payrollApi } from '@/features/payroll/api/payrollApi';
import type { EmployeeLite } from '@/features/payroll/types';
import { formatMoney } from '@/features/payroll/lib/format';
import { paygateApi, type PayoutBatch, type PayoutBatchDetail, type PayoutItem, type TransmissionEntry } from '../api/paygateApi';
import { lastProviderCheckText } from '../lib/lastProviderCheck';
import { getApiErrorMessage } from '@/lib/apiError';

type Variant = 'secondary' | 'warning' | 'info' | 'success' | 'danger';

function batchPill(status: string): { label: string; variant: Variant } {
  switch (status) {
    case 'completed': return { label: 'Completed', variant: 'success' };
    case 'partially_completed': return { label: 'Partly paid', variant: 'warning' };
    case 'submitted': return { label: 'Sent', variant: 'info' };
    case 'submitting': return { label: 'Sending', variant: 'info' };
    case 'pending_verification': return { label: 'Awaiting release', variant: 'warning' };
    case 'queued': return { label: 'Queued', variant: 'secondary' };
    case 'failed': return { label: 'Failed', variant: 'danger' };
    case 'cancelled': return { label: 'Cancelled', variant: 'secondary' };
    default: return { label: status || 'Unknown', variant: 'secondary' };
  }
}

const TRANSMISSION_META: Record<string, { label: string; variant: Variant }> = {
  uploaded: { label: 'Uploaded to bank', variant: 'info' },
  bank_received: { label: 'Bank received', variant: 'info' },
  bank_accepted: { label: 'Bank accepted', variant: 'success' },
  processing: { label: 'Processing', variant: 'warning' },
  partially_paid: { label: 'Partially paid', variant: 'warning' },
  paid: { label: 'Paid', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'danger' },
  nak: { label: 'Rejected (NAK)', variant: 'danger' },
  returned: { label: 'Returned', variant: 'warning' },
  reconciliation_required: { label: 'Needs reconciliation', variant: 'warning' },
};

function itemPill(status: string): { label: string; variant: Variant } {
  switch (status) {
    case 'paid': return { label: 'Paid', variant: 'success' };
    case 'submitted': return { label: 'Sent', variant: 'info' };
    case 'submitting': return { label: 'Sending', variant: 'info' };
    case 'queued': return { label: 'Queued', variant: 'secondary' };
    case 'reconciliation_required': return { label: 'Needs check', variant: 'warning' };
    case 'reversed': return { label: 'Reversed', variant: 'warning' };
    case 'failed': return { label: 'Failed', variant: 'danger' };
    case 'cancelled': return { label: 'Cancelled', variant: 'secondary' };
    default: return { label: status || 'Unknown', variant: 'secondary' };
  }
}

function sourceLabel(s: string): string {
  if (s.startsWith('payroll')) return 'Payroll';
  if (s.startsWith('credit')) return 'Loan';
  return s;
}
function connectorLabel(c: string): string {
  if (c === 'bankfile') return 'Bank file';
  if (c === 'azampay') return 'AzamPay';
  if (c === 'sftp') return 'Bank SFTP';
  if (c === 'nmb') return 'NMB';
  if (c === 'crdb') return 'CRDB';
  if (c === 'mpesa') return 'M-Pesa';
  if (c === 'selcom') return 'Selcom';
  return c;
}
function fmtDate(s?: string): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export const PayoutsPage: React.FC = () => {
  const [batches, setBatches] = useState<PayoutBatch[]>([]);
  const [queue, setQueue] = useState<PayoutItem[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<PayoutBatchDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [transmissions, setTransmissions] = useState<TransmissionEntry[]>([]);
  // Reconciliation (manual settle).
  const [resolving, setResolving] = useState<PayoutItem | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  // Dual-control gate: release a held batch for dispatch.
  const [releasing, setReleasing] = useState<string | null>(null);
  const { isTenantOwner, isFinance } = useRoleAccess();
  const canRelease = isTenantOwner || isFinance;

  const load = () =>
    Promise.all([
      paygateApi.listPayouts(100).catch(() => [] as PayoutBatch[]),
      paygateApi.reconciliationQueue().catch(() => [] as PayoutItem[]),
      payrollApi.listEmployees().catch(() => [] as EmployeeLite[]),
    ])
      .then(([b, q, emps]) => {
        setBatches(b);
        setQueue(q);
        setNames(new Map(emps.map((e) => [e.id, e.name])));
      })
      .finally(() => setLoading(false));

  useEffect(() => { void load(); }, []);

  const resolve = async (resolution: 'paid' | 'failed') => {
    if (!resolving) return;
    if (!note.trim()) { toast.error('Add a note — what did you verify?'); return; }
    setSaving(true);
    try {
      await paygateApi.resolveItem(resolving.id, resolution, note.trim());
      toast.success(resolution === 'paid' ? 'Marked as paid.' : 'Marked as failed.');
      setResolving(null); setNote('');
      await load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not update the payout.'));
    } finally {
      setSaving(false);
    }
  };

  const release = async (b: PayoutBatch) => {
    setReleasing(b.id);
    try {
      await paygateApi.verifyPayout(b.id);
      toast.success('Payout released — it will now be sent for processing.');
      await load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not release the payout.'));
    } finally {
      setReleasing(null);
    }
  };

  const nameFor = (ref: string) => names.get(ref) || `${ref.slice(0, 8)}…`;

  const openBatch = async (b: PayoutBatch) => {
    setDetailLoading(true);
    setDetail({ ...b, items: [] });
    setTransmissions([]);
    try {
      setDetail(await paygateApi.getPayout(b.id));
      // Transmission trail is meaningful only for the SFTP host-to-host rail.
      if (b.connector === 'sftp') {
        setTransmissions(await paygateApi.getTransmissions(b.id).catch(() => []));
      }
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const currency = useMemo(() => batches[0]?.currency ?? 'TZS', [batches]);
  const totalOut = useMemo(
    () => batches.filter((b) => b.status === 'completed' || b.status === 'partially_completed').reduce((s, b) => s + b.totalAmount, 0),
    [batches],
  );
  const held = useMemo(() => batches.filter((b) => b.status === 'pending_verification'), [batches]);
  const needsAttention = useMemo(
    () => batches.filter((b) => b.status === 'failed' || b.status === 'partially_completed' || b.status === 'pending_verification').length,
    [batches],
  );

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">Payouts</h1>
        <p className="text-muted-foreground mt-1">
          Every payment sent from your company — payroll net pay and loan disbursements — and how each employee&rsquo;s payout went.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Batches</div><div className="text-h3 text-charcoal">{batches.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Paid out</div><div className="text-h3 text-charcoal">{formatMoney(totalOut, currency)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Needs attention</div><div className="text-h3 text-charcoal">{needsAttention}</div></CardContent></Card>
      </div>

      {/* Verification gate — batches held for a dual-control release before dispatch */}
      {held.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-4">
              <ShieldCheck className="h-4 w-4 text-warning" />
              <h2 className="font-semibold text-foreground">Payouts awaiting release</h2>
              <span className="text-sm text-muted-foreground">
                {canRelease
                  ? '— verify each batch before it is sent to the bank. Once released it cannot be held again.'
                  : '— an owner or finance user must verify and release these before they are sent.'}
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Via</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Employees</TableHead>
                  <TableHead className="w-40" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {held.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="text-muted-foreground">{fmtDate(b.createdAt)}</TableCell>
                    <TableCell className="font-medium text-charcoal">
                      {sourceLabel(b.sourceSystem)}
                      {b.holdReason && <span className="mt-0.5 block text-xs font-normal text-warning">Held: {b.holdReason}</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{connectorLabel(b.connector)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-charcoal">{formatMoney(b.totalAmount, b.currency)}</TableCell>
                    <TableCell className="text-center tabular-nums text-muted-foreground">{b.itemCount}</TableCell>
                    <TableCell className="text-right">
                      {canRelease ? (
                        <Button size="sm" disabled={releasing === b.id} onClick={() => release(b)}>
                          {releasing === b.id ? 'Releasing…' : 'Verify & Release'}
                        </Button>
                      ) : (
                        <Badge variant="warning">Awaiting release</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Reconciliation queue — bank-file payouts confirmed by a human */}
      {queue.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-4">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <h2 className="font-semibold text-foreground">Payouts to confirm</h2>
              <span className="text-sm text-muted-foreground">— bank-file payments settle once you confirm your bank executed them.</span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Via</TableHead>
                  <TableHead className="w-32" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map((it) => (
                  <TableRow key={it.id}>
                    <TableCell className="font-medium text-charcoal">{nameFor(it.employeeRef)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {it.destination ? `${it.destination.type === 'BANK' ? 'Bank' : 'Mobile'} ${it.destination.accountNumber}` : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(it.amount, it.currency)}</TableCell>
                    <TableCell className="text-muted-foreground">{connectorLabel(it.connector)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => { setResolving(it); setNote(''); }}>Confirm</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Via</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Employees</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))}
              {!loading && batches.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-12 text-center text-muted-foreground">No payouts yet.</TableCell></TableRow>
              )}
              {!loading && batches.map((b) => {
                const p = batchPill(b.status);
                return (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-muted/40" onClick={() => openBatch(b)}>
                    <TableCell className="text-muted-foreground">{fmtDate(b.createdAt)}</TableCell>
                    <TableCell className="font-medium text-charcoal">{sourceLabel(b.sourceSystem)}</TableCell>
                    <TableCell className="text-muted-foreground">{connectorLabel(b.connector)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium text-charcoal">{formatMoney(b.totalAmount, b.currency)}</TableCell>
                    <TableCell className="text-center tabular-nums text-muted-foreground">{b.itemCount}</TableCell>
                    <TableCell><Badge variant={p.variant}>{p.label}</Badge></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Batch detail */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDetail(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-foreground">{sourceLabel(detail.sourceSystem)} payout · {connectorLabel(detail.connector)}</h3>
                <p className="text-sm text-muted-foreground">
                  {formatMoney(detail.totalAmount, detail.currency)} · {detail.itemCount} employees · {fmtDate(detail.createdAt)}
                </p>
              </div>
              <button onClick={() => setDetail(null)} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>

            {detailLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.items.length === 0 && (
                    <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">No line items.</TableCell></TableRow>
                  )}
                  {detail.items.map((it) => {
                    const p = itemPill(it.status);
                    const dest = it.destination;
                    return (
                      <TableRow key={it.id}>
                        <TableCell className="font-medium text-charcoal">{nameFor(it.employeeRef)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {dest ? (
                            <span className="text-xs">
                              {dest.type === 'BANK' ? 'Bank' : 'Mobile'} {dest.network ? `· ${dest.network} ` : ''}{dest.accountNumber}
                            </span>
                          ) : '—'}
                          {it.lastError ? <div className="text-xs text-danger">{it.lastError}</div> : null}
                          {(() => {
                            const check = lastProviderCheckText(it.connector, it.lastCheck);
                            return check ? (
                              <div className="text-xs text-muted-foreground" data-testid="last-provider-check">
                                {check}{it.providerRef ? ` · ref ${it.providerRef}` : ''}
                              </div>
                            ) : null;
                          })()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(it.amount, it.currency)}</TableCell>
                        <TableCell><Badge variant={p.variant}>{p.label}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {detail.connector === 'sftp' && !detailLoading && (
              <div className="mt-5">
                <h4 className="mb-2 text-sm font-semibold text-foreground">Transmission &amp; bank acknowledgement</h4>
                {transmissions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No transmission events yet — the file is queued or awaiting the bank's return file.</p>
                ) : (
                  <ol className="space-y-2">
                    {transmissions.map((t, i) => {
                      const meta = TRANSMISSION_META[t.state] ?? { label: t.state, variant: 'secondary' as const };
                      return (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Badge variant={meta.variant}>{meta.label}</Badge>
                          <div className="min-w-0">
                            <div className="text-xs text-muted-foreground">{fmtDate(t.occurred_at)}</div>
                            {t.detail ? <div className="text-xs text-charcoal">{t.detail}</div> : null}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Confirm / resolve a payout item */}
      {resolving && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setResolving(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-foreground">Confirm payout</h3>
                <p className="text-sm text-muted-foreground">{nameFor(resolving.employeeRef)} · {formatMoney(resolving.amount, resolving.currency)}</p>
              </div>
              <button onClick={() => setResolving(null)} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
            <label className="mb-1 block text-sm font-medium text-foreground">Note (what you verified)</label>
            <input
              className="mb-1 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100"
              value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. confirmed in bank statement, ref 12345" />
            <p className="mb-4 text-xs text-muted-foreground">Recorded on the audit trail.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" className="text-danger hover:bg-red-50 hover:text-red-700" disabled={saving} onClick={() => resolve('failed')}>Mark failed</Button>
              <Button disabled={saving} onClick={() => resolve('paid')}>{saving ? 'Saving…' : 'Mark paid'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsPage;
